import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { api, resolveImageUrl } from '@/lib/axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import {
  ArrowLeft,
  Printer,
  Edit2,
  Copy,
  Check,
  CreditCard,
  Truck,
  Package,
  Clock,
  CheckCircle2,
  MessageCircle,
  Image as ImageIcon,
} from 'lucide-react';
import { formatWhatsAppNumber } from '@/lib/constants';

interface OrderItemData {
  id?: number;
  productId?: number;
  productName: string;
  quantity: number;
  unitPrice: number;
  filamentColor?: string;
  variantNotes?: string;
  product?: {
    id: number;
    name: string;
    mainImageUrl?: string;
    slug?: string;
  };
}

interface OrderData {
  id: number;
  guestName?: string;
  guestWhatsApp?: string;
  guestEmail?: string;
  guestAddress?: string;
  customer?: {
    id: number;
    fullName: string;
    phoneNumber?: string;
    email?: string;
    address?: string;
  };
  deliveryMethod?: string;
  status: string;
  paymentStatus: string;
  paymentMethod?: string;
  subtotal: number;
  discount: number;
  total: number;
  notes?: string;
  createdAt: string;
  items?: OrderItemData[];
}

const DELIVERY_STEPS = [
  { key: 'pending', label: 'En Preparación', icon: Clock },
  { key: 'in_production', label: 'En Producción 3D', icon: Package },
  { key: 'shipped', label: 'Enviado / En Camino', icon: Truck },
  { key: 'delivered', label: 'Entregado', icon: CheckCircle2 },
];

export default function OrderDetailPageAdmin() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [order, setOrder] = useState<OrderData | null>(null);
  const [allOrders, setAllOrders] = useState<OrderData[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  // Modal para editar orden
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editStatus, setEditStatus] = useState('pending');
  const [editPaymentStatus, setEditPaymentStatus] = useState('pending');
  const [editPaymentMethod, setEditPaymentMethod] = useState('MercadoPago');
  const [editNotes, setEditNotes] = useState('');

  // Copied indicator
  const [copiedField, setCopiedField] = useState<string | null>(null);

  useEffect(() => {
    loadData();
  }, [id]);

  const loadData = async () => {
    try {
      setIsLoading(true);
      const ordersRes = await api.get<{ items: OrderData[] }>('/orders?pageSize=50').catch(() => ({ data: { items: [] } }));
      const orderList = Array.isArray(ordersRes.data?.items) ? ordersRes.data.items : [];
      setAllOrders(orderList);

      let targetId = id;
      if (!targetId || targetId === 'detail') {
        if (orderList.length > 0) {
          targetId = String(orderList[0].id);
        }
      }

      if (!targetId || targetId === 'detail') {
        setOrder(null);
        return;
      }

      const res = await api.get<OrderData>(`/orders/${targetId}`);
      setOrder(res.data);
      setEditStatus(res.data.status || 'pending');
      setEditPaymentStatus(res.data.paymentStatus || 'pending');
      setEditPaymentMethod(res.data.paymentMethod || 'MercadoPago');
      setEditNotes(res.data.notes || '');
    } catch {
      toast.error('Error al cargar detalle del pedido');
    } finally {
      setIsLoading(false);
    }
  };

  const copyToClipboard = (text: string, field: string) => {
    navigator.clipboard.writeText(text);
    setCopiedField(field);
    toast.success(`${field} copiado al portapapeles`);
    setTimeout(() => setCopiedField(null), 2000);
  };

  const handlePrint = () => {
    window.print();
  };

  const handleSaveEdit = async () => {
    if (!order) return;
    try {
      await api.patch(`/orders/${order.id}/status`, { status: editStatus });
      await api.put(`/orders/${order.id}`, {
        ...order,
        status: editStatus,
        paymentStatus: editPaymentStatus,
        paymentMethod: editPaymentMethod,
        notes: editNotes,
      });
      toast.success('Pedido actualizado con éxito');
      setIsEditModalOpen(false);
      loadData();
    } catch {
      toast.error('No se pudo guardar la modificación');
    }
  };

  const handleQuickStatusChange = async (nextStatus: string) => {
    if (!order) return;
    try {
      await api.patch(`/orders/${order.id}/status`, { status: nextStatus });
      setOrder((prev) => (prev ? { ...prev, status: nextStatus } : prev));
      toast.success(`Estado actualizado a: ${nextStatus}`);
    } catch {
      toast.error('Error al cambiar estado');
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-24 text-muted-foreground">
        Cargando detalle del pedido...
      </div>
    );
  }

  if (!order) {
    return (
      <div className="text-center py-20 space-y-4 font-sans">
        <h2 className="text-2xl font-bold">No hay pedidos disponibles</h2>
        <p className="text-sm text-muted-foreground">
          Aún no se han registrado pedidos en el sistema.
        </p>
        <Button asChild variant="outline">
          <Link to="/orders">
            <ArrowLeft className="h-4 w-4 mr-2" /> Volver a Lista de Pedidos
          </Link>
        </Button>
      </div>
    );
  }

  const customerName = order.customer?.fullName || order.guestName || 'Cliente Particular';
  const customerEmail = order.customer?.email || order.guestEmail || 'sin-email@miladesign.com';
  const customerPhone = order.customer?.phoneNumber || order.guestWhatsApp || 'Sin teléfono';
  const customerAddress = order.customer?.address || order.guestAddress || 'Retiro en taller / A convenir';

  const orderDate = new Date(order.createdAt).toLocaleDateString('es-AR', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });

  const getStepIndex = (status: string) => {
    switch (status) {
      case 'pending':
        return 0;
      case 'in_production':
        return 1;
      case 'shipped':
        return 2;
      case 'delivered':
        return 3;
      default:
        return 0;
    }
  };

  const currentStep = getStepIndex(order.status);
  const itemsCount = order.items?.reduce((acc, i) => acc + i.quantity, 0) || 1;
  const subtotal = order.subtotal || order.total;
  const discount = order.discount || 0;
  const shipping = order.deliveryMethod === 'envio' ? 3500 : 0;
  const finalTotal = order.total;

  return (
    <>
      {/* ── INTERFAZ DE ADMINISTRACIÓN (PANTALLA) ── */}
      <div className="space-y-6 max-w-7xl mx-auto pb-16 font-sans print:hidden">
        {/* Header Superior: Back, Selector, Print, Edit */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pb-2 border-b border-border/60">
          <div className="flex items-center gap-3">
            <Button asChild variant="ghost" size="icon" className="h-9 w-9 rounded-lg">
              <Link to="/orders">
                <ArrowLeft className="h-4 w-4" />
              </Link>
            </Button>
            <div>
              <div className="flex items-center gap-2">
                <h1 className="text-2xl md:text-3xl font-bold tracking-tight text-foreground">
                  Detalle del Pedido #{order.id}
                </h1>
                <Badge
                  variant="outline"
                  className={`text-xs font-semibold ${
                    order.status === 'delivered'
                      ? 'bg-emerald-500/10 text-emerald-600 border-emerald-500/30'
                      : order.status === 'shipped'
                      ? 'bg-blue-500/10 text-blue-600 border-blue-500/30'
                      : order.status === 'in_production'
                      ? 'bg-purple-500/10 text-purple-600 border-purple-500/30'
                      : 'bg-amber-500/10 text-amber-600 border-amber-500/30'
                  }`}
                >
                  {order.status === 'delivered'
                    ? 'Entregado'
                    : order.status === 'shipped'
                    ? 'Enviado'
                    : order.status === 'in_production'
                    ? 'En Producción'
                    : 'Pendiente'}
                </Badge>
              </div>
              <p className="text-xs text-muted-foreground mt-0.5">
                Realizado el {orderDate} • Canal: {order.deliveryMethod || 'Tienda Web'}
              </p>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            {allOrders.length > 1 && (
              <div className="flex items-center gap-1.5 bg-muted/40 border border-border rounded-lg px-2.5 py-1">
                <span className="text-[11px] text-muted-foreground whitespace-nowrap">Ver Pedido:</span>
                <select
                  value={order.id}
                  onChange={(e) => navigate(`/orders/${e.target.value}`)}
                  className="text-xs bg-transparent font-medium text-foreground focus:outline-none cursor-pointer max-w-[150px] truncate"
                >
                  {allOrders.map((o) => (
                    <option key={o.id} value={o.id} className="bg-popover text-popover-foreground">
                      #{o.id} - {o.customer?.fullName || o.guestName || 'Cliente'} (${o.total})
                    </option>
                  ))}
                </select>
              </div>
            )}
            <Button
              variant="outline"
              size="sm"
              onClick={handlePrint}
              className="gap-1.5 text-xs font-semibold shadow-2xs"
            >
              <Printer className="h-3.5 w-3.5" /> Imprimir Comprobante
            </Button>
            <Button
              size="sm"
              onClick={() => setIsEditModalOpen(true)}
              className="gap-1.5 text-xs font-semibold bg-foreground text-background hover:bg-foreground/90 shadow-2xs"
            >
              <Edit2 className="h-3.5 w-3.5" /> Editar Pedido
            </Button>
          </div>
        </div>

        {/* ── FILA SUPERIOR: Card Pedido / Cliente + Card Resumen ── */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Card Pedido e Información del Cliente */}
          <Card className="lg:col-span-2 border-border/70 shadow-2xs">
            <CardHeader className="pb-3 flex flex-row items-center justify-between border-b border-border/40">
              <div>
                <CardTitle className="text-base font-bold">
                  Pedido ORD-{order.id.toString().padStart(5, '0')}
                </CardTitle>
                <p className="text-xs text-muted-foreground mt-0.5">
                  ID de referencia interna Mila Design Studio
                </p>
              </div>
              <span className="text-xs font-medium text-muted-foreground">
                Registrado el {orderDate}
              </span>
            </CardHeader>
            <CardContent className="pt-4 space-y-4">
              <div>
                <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">
                  Información del Cliente
                </h4>
                <div className="space-y-1 text-sm">
                  <div className="font-semibold text-foreground">{customerName}</div>
                  <div className="text-xs text-muted-foreground">{customerEmail}</div>
                  <div className="text-xs text-muted-foreground">{customerAddress}</div>
                </div>
              </div>

              {/* Inset Subcard: Payment Method */}
              <div className="p-3.5 rounded-xl border border-border bg-muted/30 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-background border border-border text-foreground">
                    <CreditCard className="h-4 w-4" />
                  </div>
                  <div>
                    <div className="text-xs font-semibold text-foreground">
                      Método de Pago: {order.paymentMethod || 'MercadoPago / Transferencia'}
                    </div>
                    <div className="text-[11px] text-muted-foreground">
                      Estado: <span className="font-medium text-foreground">{order.paymentStatus === 'approved' ? 'Aprobado' : 'Pendiente de acreditación'}</span>
                    </div>
                  </div>
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 text-muted-foreground hover:text-foreground"
                  onClick={() => setIsEditModalOpen(true)}
                  title="Cambiar método de pago"
                >
                  <Edit2 className="h-3.5 w-3.5" />
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Card Resumen del Pedido (Order Summary) */}
          <Card className="border-border/70 shadow-2xs">
            <CardHeader className="pb-3 border-b border-border/40">
              <CardTitle className="text-base font-bold">Resumen del Pedido</CardTitle>
            </CardHeader>
            <CardContent className="pt-4 space-y-3 text-xs">
              <div className="flex justify-between items-center text-muted-foreground">
                <span>Subtotal ({itemsCount} {itemsCount === 1 ? 'ítem' : 'ítems'})</span>
                <span className="font-medium text-foreground">${subtotal.toLocaleString('es-AR')}</span>
              </div>
              {discount > 0 && (
                <div className="flex justify-between items-center text-emerald-600">
                  <span>Descuento Promocional</span>
                  <span className="font-medium">-${discount.toLocaleString('es-AR')}</span>
                </div>
              )}
              <div className="flex justify-between items-center text-muted-foreground">
                <span>Costo de Envío</span>
                <span className="font-medium text-foreground">
                  {shipping > 0 ? `$${shipping.toLocaleString('es-AR')}` : 'Gratis / A convenir'}
                </span>
              </div>
              <div className="flex justify-between items-center text-muted-foreground">
                <span>Impuestos (0%)</span>
                <span className="font-medium text-foreground">$0.00</span>
              </div>
              <div className="pt-3 border-t border-border flex justify-between items-baseline">
                <span className="text-sm font-bold text-foreground">Total</span>
                <span className="text-xl font-bold text-primary font-mono">
                  ${finalTotal.toLocaleString('es-AR')}
                </span>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* ── FILA INTERMEDIA: Delivery Status con Línea de Progreso ── */}
        <Card className="border-border/70 shadow-2xs">
          <CardHeader className="pb-2 flex flex-row items-center justify-between">
            <CardTitle className="text-base font-bold">Estado de la Entrega</CardTitle>
            <Badge variant="outline" className="text-xs bg-muted text-muted-foreground">
              Actualizado: {orderDate}
            </Badge>
          </CardHeader>
          <CardContent className="pt-2">
            {/* Stepper Timeline */}
            <div className="relative pt-4 pb-2">
              <div className="hidden sm:block absolute top-8 left-10 right-10 h-0.5 bg-border -z-0" />
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 relative z-10">
                {DELIVERY_STEPS.map((step, idx) => {
                  const Icon = step.icon;
                  const isCompleted = idx <= currentStep;
                  const isCurrent = idx === currentStep;

                  return (
                    <div
                      key={step.key}
                      onClick={() => handleQuickStatusChange(step.key)}
                      className="flex flex-col items-center text-center cursor-pointer group"
                    >
                      <div
                        className={`h-10 w-10 rounded-full flex items-center justify-center transition-all ${
                          isCompleted
                            ? 'bg-emerald-600 text-white shadow-xs'
                            : 'bg-muted border border-border text-muted-foreground group-hover:border-primary'
                        } ${isCurrent ? 'ring-4 ring-emerald-500/20' : ''}`}
                      >
                        <Icon className="h-5 w-5" />
                      </div>
                      <span
                        className={`text-xs mt-2 font-medium ${
                          isCompleted ? 'text-foreground font-semibold' : 'text-muted-foreground'
                        }`}
                      >
                        {step.label}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* ── FILA INFERIOR: Items del Pedido + Detalles del Cliente ── */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Card: Order Items (col-span-2) */}
          <Card className="lg:col-span-2 border-border/70 shadow-2xs">
            <CardHeader className="pb-3 border-b border-border/40">
              <CardTitle className="text-base font-bold">Productos del Pedido</CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="border-b border-border/60 text-muted-foreground text-left bg-muted/20">
                      <th className="py-2.5 px-4 font-medium">Producto</th>
                      <th className="py-2.5 px-3 font-medium text-center">Cantidad</th>
                      <th className="py-2.5 px-3 font-medium text-right">Precio Unitario</th>
                      <th className="py-2.5 px-4 font-medium text-right">Total</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border/40">
                    {order.items && order.items.length > 0 ? (
                      order.items.map((item, idx) => {
                        const img = item.product?.mainImageUrl
                          ? resolveImageUrl(item.product.mainImageUrl)
                          : null;
                        const lineTotal = item.quantity * item.unitPrice;

                        return (
                          <tr key={idx} className="hover:bg-muted/10 transition-colors">
                            <td className="py-3 px-4">
                              <div className="flex items-center gap-3">
                                <div className="h-11 w-11 rounded-lg border border-border bg-muted/30 overflow-hidden shrink-0 flex items-center justify-center">
                                  {img ? (
                                    <img
                                      src={img}
                                      alt={item.productName}
                                      className="w-full h-full object-cover"
                                    />
                                  ) : (
                                    <ImageIcon className="h-5 w-5 text-muted-foreground/40" />
                                  )}
                                </div>
                                <div>
                                  <div className="font-semibold text-foreground text-xs">
                                    {item.productName}
                                  </div>
                                  {(item.filamentColor || item.variantNotes) && (
                                    <div className="text-[11px] text-muted-foreground">
                                      {item.filamentColor && (
                                        <span>Color: {item.filamentColor}</span>
                                      )}
                                      {item.variantNotes && (
                                        <span className="ml-1.5">• {item.variantNotes}</span>
                                      )}
                                    </div>
                                  )}
                                </div>
                              </div>
                            </td>
                            <td className="py-3 px-3 text-center font-medium text-foreground">
                              {item.quantity}
                            </td>
                            <td className="py-3 px-3 text-right font-medium text-muted-foreground font-mono">
                              ${item.unitPrice.toLocaleString('es-AR')}
                            </td>
                            <td className="py-3 px-4 text-right font-bold text-foreground font-mono">
                              ${lineTotal.toLocaleString('es-AR')}
                            </td>
                          </tr>
                        );
                      })
                    ) : (
                      <tr>
                        <td colSpan={4} className="py-8 text-center text-xs text-muted-foreground">
                          Pedido sin ítems registrados.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>

          {/* Card: Customer Details (col-span-1) */}
          <Card className="border-border/70 shadow-2xs">
            <CardHeader className="pb-3 border-b border-border/40">
              <CardTitle className="text-base font-bold">Detalles del Cliente</CardTitle>
            </CardHeader>
            <CardContent className="pt-4 space-y-4 text-xs">
              <div>
                <span className="text-[11px] text-muted-foreground block">Nombre y Apellido</span>
                <span className="font-semibold text-foreground text-sm">{customerName}</span>
              </div>

              <div>
                <span className="text-[11px] text-muted-foreground block">Correo Electrónico</span>
                <div className="flex items-center justify-between mt-0.5">
                  <span className="font-medium text-foreground truncate mr-2">{customerEmail}</span>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7 text-muted-foreground hover:text-foreground shrink-0"
                    onClick={() => copyToClipboard(customerEmail, 'Email')}
                    title="Copiar email"
                  >
                    {copiedField === 'Email' ? (
                      <Check className="h-3.5 w-3.5 text-emerald-600" />
                    ) : (
                      <Copy className="h-3.5 w-3.5" />
                    )}
                  </Button>
                </div>
              </div>

              <div>
                <span className="text-[11px] text-muted-foreground block">Teléfono / WhatsApp</span>
                <div className="flex items-center justify-between mt-0.5">
                  <span className="font-medium text-foreground">{customerPhone}</span>
                  <div className="flex items-center gap-1">
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7 text-muted-foreground hover:text-foreground"
                      onClick={() => copyToClipboard(customerPhone, 'Teléfono')}
                      title="Copiar teléfono"
                    >
                      {copiedField === 'Teléfono' ? (
                        <Check className="h-3.5 w-3.5 text-emerald-600" />
                      ) : (
                        <Copy className="h-3.5 w-3.5" />
                      )}
                    </Button>
                    {customerPhone && customerPhone !== 'Sin teléfono' && (
                      <Button
                        variant="outline"
                        size="icon"
                        className="h-7 w-7 text-emerald-600 border-emerald-500/30 hover:bg-emerald-500/10"
                        asChild
                        title="Abrir WhatsApp"
                      >
                        <a
                          href={`https://wa.me/${formatWhatsAppNumber(customerPhone)}?text=${encodeURIComponent(`¡Hola ${customerName}! Nos comunicamos desde Mila Design Studio por tu pedido #${order.id}.`)}`}
                          target="_blank"
                          rel="noreferrer"
                        >
                          <MessageCircle className="h-3.5 w-3.5" />
                        </a>
                      </Button>
                    )}
                  </div>
                </div>
              </div>

              <div>
                <span className="text-[11px] text-muted-foreground block">Dirección de Entrega</span>
                <div className="flex items-start justify-between mt-0.5">
                  <span className="font-medium text-foreground">{customerAddress}</span>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7 text-muted-foreground hover:text-foreground shrink-0"
                    onClick={() => copyToClipboard(customerAddress, 'Dirección')}
                    title="Copiar dirección"
                  >
                    {copiedField === 'Dirección' ? (
                      <Check className="h-3.5 w-3.5 text-emerald-600" />
                    ) : (
                      <Copy className="h-3.5 w-3.5" />
                    )}
                  </Button>
                </div>
              </div>

              {order.notes && (
                <div className="pt-2 border-t border-border/50">
                  <span className="text-[11px] text-muted-foreground block">Notas del Pedido</span>
                  <p className="italic text-foreground mt-0.5 bg-muted/30 p-2 rounded-lg">
                    &quot;{order.notes}&quot;
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* ── MODAL: EDITAR PEDIDO ── */}
      <Dialog open={isEditModalOpen} onOpenChange={setIsEditModalOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-base font-bold">Editar Pedido #{order.id}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2 text-xs">
            <div className="space-y-1.5">
              <Label className="text-xs">Estado de la Entrega</Label>
              <select
                value={editStatus}
                onChange={(e) => setEditStatus(e.target.value)}
                className="w-full h-9 px-3 rounded-lg border border-border bg-background text-xs"
              >
                <option value="pending">Pendiente</option>
                <option value="in_production">En Producción 3D</option>
                <option value="shipped">Enviado / En Camino</option>
                <option value="delivered">Entregado</option>
                <option value="cancelled">Cancelado</option>
              </select>
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs">Estado del Pago</Label>
              <select
                value={editPaymentStatus}
                onChange={(e) => setEditPaymentStatus(e.target.value)}
                className="w-full h-9 px-3 rounded-lg border border-border bg-background text-xs"
              >
                <option value="approved">Aprobado / Pagado</option>
                <option value="pending">Pendiente de Pago</option>
                <option value="rejected">Rechazado</option>
              </select>
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs">Método de Pago</Label>
              <Input
                value={editPaymentMethod}
                onChange={(e) => setEditPaymentMethod(e.target.value)}
                placeholder="ej. MercadoPago, Transferencia, Efectivo"
                className="h-9 text-xs"
              />
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs">Notas Adicionales</Label>
              <Input
                value={editNotes}
                onChange={(e) => setEditNotes(e.target.value)}
                placeholder="Indicaciones para taller o despacho..."
                className="h-9 text-xs"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" size="sm" onClick={() => setIsEditModalOpen(false)}>
              Cancelar
            </Button>
            <Button size="sm" onClick={handleSaveEdit}>
              Guardar Cambios
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── HOJA DE IMPRESIÓN (PDF / PRINT VOUCHER) ── */}
      {/* Esta sección se activa automáticamente al llamar a window.print() o presionar 'Imprimir Comprobante' */}
      <div className="hidden print:block p-8 bg-white text-black font-sans min-h-screen">
        {/* Cabecera */}
        <div className="flex justify-between items-start border-b-2 border-black pb-4 mb-6">
          <div>
            <h1 className="text-3xl font-extrabold tracking-tight">MILA DESIGN STUDIO</h1>
            <p className="text-xs uppercase tracking-widest text-gray-600 mt-1">
              Impresión 3D • Lámparas & Objetos Japandi Sustentables
            </p>
            <p className="text-xs text-gray-500 mt-0.5">
              www.miladesign3d.com • WhatsApp: +54 9 11 5926-5776
            </p>
          </div>
          <div className="text-right">
            <h2 className="text-2xl font-bold font-mono">PEDIDO #{order.id}</h2>
            <p className="text-xs text-gray-600 mt-1">Fecha: {orderDate}</p>
            <div className="mt-2 inline-block px-2.5 py-1 bg-gray-100 border border-gray-300 text-[10px] font-bold rounded">
              {order.paymentStatus === 'approved' ? 'PAGO ACREDITADO' : 'PAGO PENDIENTE'}
            </div>
          </div>
        </div>

        {/* Disclaimer Oficial Solicitado por el Usuario */}
        <div className="p-2.5 mb-6 bg-gray-100 border border-dashed border-gray-400 rounded text-center">
          <p className="text-[11px] font-bold text-gray-800 tracking-wider">
            DOCUMENTO NO VÁLIDO COMO FACTURA FISCAL — COMPROBANTE DE PEDIDO / REMITO INTERNO
          </p>
          <p className="text-[10px] text-gray-500">
            Documento emitido exclusivamente a efectos de control de producción, entrega y recepción de mercadería.
          </p>
        </div>

        {/* Datos del Cliente y Envío */}
        <div className="grid grid-cols-2 gap-6 p-4 border border-gray-300 rounded mb-6 text-xs">
          <div>
            <h3 className="font-bold text-gray-800 uppercase tracking-wider mb-2">
              Datos del Destinatario
            </h3>
            <p><span className="font-semibold">Cliente:</span> {customerName}</p>
            <p><span className="font-semibold">Email:</span> {customerEmail}</p>
            <p><span className="font-semibold">Teléfono:</span> {customerPhone}</p>
          </div>
          <div>
            <h3 className="font-bold text-gray-800 uppercase tracking-wider mb-2">
              Datos de Despacho & Pago
            </h3>
            <p><span className="font-semibold">Dirección:</span> {customerAddress}</p>
            <p><span className="font-semibold">Método de Envío:</span> {order.deliveryMethod || 'A convenir'}</p>
            <p><span className="font-semibold">Medio de Pago:</span> {order.paymentMethod || 'MercadoPago'}</p>
          </div>
        </div>

        {/* Tabla de Productos */}
        <table className="w-full text-xs border-collapse border border-gray-300 mb-6">
          <thead>
            <tr className="bg-gray-100 border-b border-gray-300 text-left">
              <th className="p-2.5 border-r border-gray-300 font-bold">Cant.</th>
              <th className="p-2.5 border-r border-gray-300 font-bold">Descripción del Producto</th>
              <th className="p-2.5 border-r border-gray-300 font-bold">Detalle / Color</th>
              <th className="p-2.5 border-r border-gray-300 font-bold text-right">Precio Unit.</th>
              <th className="p-2.5 font-bold text-right">Subtotal</th>
            </tr>
          </thead>
          <tbody>
            {order.items && order.items.length > 0 ? (
              order.items.map((item, idx) => (
                <tr key={idx} className="border-b border-gray-200">
                  <td className="p-2.5 border-r border-gray-300 text-center font-bold">
                    {item.quantity}
                  </td>
                  <td className="p-2.5 border-r border-gray-300 font-semibold">
                    {item.productName}
                  </td>
                  <td className="p-2.5 border-r border-gray-300 text-gray-600">
                    {item.filamentColor || item.variantNotes || 'Estándar'}
                  </td>
                  <td className="p-2.5 border-r border-gray-300 text-right font-mono">
                    ${item.unitPrice.toLocaleString('es-AR')}
                  </td>
                  <td className="p-2.5 text-right font-mono font-bold">
                    ${(item.quantity * item.unitPrice).toLocaleString('es-AR')}
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={5} className="p-4 text-center text-gray-500">
                  Sin ítems especificados.
                </td>
              </tr>
            )}
          </tbody>
        </table>

        {/* Resumen de Totales */}
        <div className="flex justify-end mb-12">
          <div className="w-64 text-xs space-y-1.5 border border-gray-300 p-3 rounded">
            <div className="flex justify-between">
              <span className="text-gray-600">Subtotal:</span>
              <span className="font-mono">${subtotal.toLocaleString('es-AR')}</span>
            </div>
            {discount > 0 && (
              <div className="flex justify-between text-gray-600">
                <span>Descuento:</span>
                <span className="font-mono">-${discount.toLocaleString('es-AR')}</span>
              </div>
            )}
            <div className="flex justify-between">
              <span className="text-gray-600">Envío:</span>
              <span className="font-mono">${shipping.toLocaleString('es-AR')}</span>
            </div>
            <div className="border-t border-gray-400 pt-1.5 flex justify-between text-sm font-bold">
              <span>TOTAL A PAGAR:</span>
              <span className="font-mono">${finalTotal.toLocaleString('es-AR')}</span>
            </div>
          </div>
        </div>

        {/* Firma de Recepción */}
        <div className="grid grid-cols-2 gap-12 pt-8 border-t border-gray-300 text-xs">
          <div className="text-center pt-8 border-t border-gray-400 mt-8">
            <p className="font-bold">Mila Design Studio</p>
            <p className="text-gray-500 text-[10px]">Taller de Diseño & Fabricación 3D</p>
          </div>
          <div className="text-center pt-8 border-t border-gray-400 mt-8">
            <p className="font-bold">Firma y Aclaración de Quien Recibe</p>
            <p className="text-gray-500 text-[10px]">DNI / Conformidad de Entrega</p>
          </div>
        </div>
      </div>
    </>
  );
}
